import { safeRuleLabel } from "./fixture-placement-rules.js";
//#region src/fixture-public-route-rules.ts
/**
* A route carries `@Public()` and no declaration in this fixture makes it legal.
*
* ⛔ A gate name. Never renamed for prose.
*/
const PUBLIC_ROUTE_UNDECLARED_RULE = "public-route-undeclared";
/**
* A route carries `@Public()` and a declaration DOES make it legal. Reported anyway, at `warning`.
*
* ⛔ NOT A COMPLAINT, AND DO NOT DELETE IT AS NOISE. The requirement is that validate "print VERY
* LOUDLY any public route defined in an app", not only the illegal ones. A route that skips the
* auth guard is the highest-consequence thing an app can declare, and the failure this guards
* against is the quiet one: a legal `@Public()` route added years ago, never printed again, and
* therefore never re-examined when the thing it serves changes. An inventory that only appears
* when something is broken is not an inventory.
*
* ⛔ A gate name. Never renamed for prose.
*/
const PUBLIC_ROUTE_DECLARED_RULE = "public-route-declared";
/**
* The shortest `reason` this rule will accept, in trimmed characters.
*
* ⛔ A FLOOR, BECAUSE A REQUIRED FIELD THAT ACCEPTS `"x"` IS REQUIRED IN NAME ONLY (ruling R-8).
* The number is a judgement call and is deliberately in one place so it can be moved: 30 is about
* the shortest string that can name what the caller is and why it holds no credential. The worked
* example, the OAuth consent callback's `#552` justification, is roughly 140 characters.
*
* ⚠️ Mirrored as `minLength` on `$defs/unguardedExposeRule` in `fixture.schema.json`. The two must
* move together: the schema refuses a short reason at every door that Ajv-validates a manifest,
* and this rule refuses it with a message that says what to write instead.
*/
const UNGUARDED_REASON_MIN_LENGTH = 30;
/**
* The longest `reason` this module will render into a message.
*
* ⛔ WHY THIS EXISTS INSTEAD OF `safeRuleLabel`, WHICH EVERY OTHER VALUE IN THIS FILE USES.
* `safeRuleLabel` caps at 64 characters (`RULE_LABEL_MAX`), which is right for a path or a class
* name, where the label only has to identify something the reader can go and look at. **It is
* wrong for `reason`, because the reason is not a label: it IS the payload.** The worked example,
* the OAuth consent callback's `#552` justification, is ~190 characters, so `safeRuleLabel` cuts it
* mid-sentence and the inventory line reports a truncated fragment of the one sentence the field
* exists to carry. Caught by a test asserting the message contains the author's whole reason.
*
* ⚠️ Still BOUNDED and still sanitised. A manifest is untrusted input, so an unbounded reason
* would let an author flood a CI log, and a control character would let one forge output.
*/
const REASON_RENDER_MAX = 300;
/**
* A `reason` rendered for display: printable ASCII only, bounded at {@link REASON_RENDER_MAX}.
*
* ⛔ SANITISED FOR DISPLAY ONLY. The VERDICT is computed on the raw value by
* {@link isUsableUnguardedReason}, which is the convention every sibling rule in this package
* follows. Never let the rendering change the answer.
*/
function safeReason(reason) {
	const cleaned = reason.trim().replace(/[^\x20-\x7E]/g, "?");
	return cleaned.length > REASON_RENDER_MAX ? `${cleaned.slice(0, REASON_RENDER_MAX)}...` : cleaned;
}
/**
* Put a path into the one form both sides compare in: exactly one leading slash, no repeated
* slashes, no trailing slash, and `/` for anything empty.
*
* ⭐ EXPORTED because the discoverer must apply it too, and applying it in only one place is the
* whole failure mode. MEASURED against the real app repo: `@Controller()` with no argument beside
* `@Get('/health')` joins to `//health`, while the manifest declares `health.ready: "/health"`.
* Those are the same route, and a comparison without this function scores them different.
*/
function normalizeRoutePath(raw) {
	const collapsed = raw.trim().replace(/\/+/g, "/");
	const withLead = collapsed.startsWith("/") ? collapsed : `/${collapsed}`;
	return withLead.length > 1 && withLead.endsWith("/") ? withLead.slice(0, -1) : withLead;
}
/**
* The path a `FixtureProbe` names, or `null` when it names none.
*
* ⛔ TWO OF THE FOUR PROBE FORMS DECLARE NO PATH AND CAN THEREFORE LEGALISE NOTHING.
* `FixtureProbe` is `string | {type:'http',path,port?} | {type:'tcp',port} | {type:'exec',command}`.
* A `tcp` probe names a port and an `exec` probe names a command, so neither says anything about
* an HTTP route. Reading a path off them would either throw or, worse, admit `undefined` into the
* legal set, where it would match nothing and look like a working check.
*/
function probePath(probe) {
	if (typeof probe === "string") return probe === "" ? null : normalizeRoutePath(probe);
	if (probe === null || typeof probe !== "object") return null;
	const p = probe;
	if (p.type !== "http") return null;
	return typeof p.path === "string" && p.path !== "" ? normalizeRoutePath(p.path) : null;
}
/** Both probe slots of one `health` block, as normalized paths. */
function healthBlockPaths(health, into) {
	if (health === null || typeof health !== "object") return;
	const h = health;
	for (const probe of [h.ready, h.live]) {
		const path = probePath(probe);
		if (path !== null) into.add(path);
	}
}
/**
* The HTTP methods a declared health probe legalises.
*
* ⛔ GET ONLY, AND THE CHANNEL WAS METHOD-BLIND UNTIL THIS EXISTED. A declared `health.ready:
* "/health"` used to legalise EVERY verb on that path, so `@Delete('/health') @Public()` was
* silently legal — and this is the channel with NO written reason attached, while `expose.unguarded`
* is method-aware AND carries a reason floor. **The weaker channel was the one asking for less
* justification**, which is backwards.
*
* ⭐ GET is not a guess: a Kubernetes `httpGet` probe issues GET, so a probe declaration cannot
* justify any other verb. MEASURED across every `fixture.json` in the app repo before narrowing:
* every declared probe path is served by an `@Get` handler and none by another verb, so this
* refuses nothing that exists. The `tcp`/`exec` probe forms declare no path and never reach here.
*
* ⇒ An author who genuinely needs another verb on a probe path declares it in `expose.unguarded`
* with a reason, which is the whole point: the exemption that costs nothing should be the narrowest.
*/
const HEALTH_PROBE_METHODS = /* @__PURE__ */ new Set(["GET"]);
/**
* Every health-probe path this manifest declares, normalized.
*
* ⛔ BOTH DECLARATION SITES, and there are two. `FixtureService.health` and
* `FixtureContainer.health` are separate optional members of the contract with the same shape, and
* a multi-container service declares its probe on the container that serves it. Reading only the
* service level would refuse a legitimate probe on a Bucket 3 fixture.
*
* ⭐ EXPORTED so the runtime gate can compute the identical set from the identical manifest.
*
* @param manifest a fixture manifest, or anything at all.
*/
function declaredHealthProbePaths(manifest) {
	const out = /* @__PURE__ */ new Set();
	if (manifest === null || typeof manifest !== "object") return out;
	const services = manifest.services;
	if (!Array.isArray(services)) return out;
	for (const svc of services) {
		if (svc === null || typeof svc !== "object") continue;
		const s = svc;
		healthBlockPaths(s.health, out);
		if (!Array.isArray(s.containers)) continue;
		for (const container of s.containers) {
			if (container === null || typeof container !== "object") continue;
			healthBlockPaths(container.health, out);
		}
	}
	return out;
}
/**
* Every `apiPaths` prefix any service in this fixture declares, normalized.
*
* Across ALL services, for the reason `fixture-capability-rules.ts` gives for the same walk: a
* fixture's services share one namespace, and the route's own service is not identifiable from
* source.
*/
function declaredApiPathPrefixes(manifest) {
	const m = manifest !== null && typeof manifest === "object" ? manifest : {};
	const services = Array.isArray(m.services) ? m.services : [];
	const out = [];
	for (const svc of services) {
		if (svc === null || typeof svc !== "object") continue;
		const paths = svc.apiPaths;
		if (!Array.isArray(paths)) continue;
		for (const p of paths) if (typeof p === "string" && p !== "") out.push(normalizeRoutePath(p));
	}
	return out;
}
/**
* The paths a route could be DECLARED as, given this fixture's `apiPaths`.
*
* ⛔ THE WHOLE REASON THIS FUNCTION EXISTS: a discoverer reading source sees the IN-APP path
* (`/delegation/consent/callback`), and an author writes `expose.*` entries as SERVED paths
* (`/v1/zion-ai/directory/delegation/consent/callback`), because that is how every existing
* `expose.public` entry is written. Comparing the two directly never matches, and the rule would
* refuse every correct declaration.
*
* ⛔ AND IT IS AN EXACT SET, NOT A SUFFIX TEST. A suffix test would let a declaration of
* `/v1/x/foo/bar` legalise an unrelated in-app route `/bar`. Building the candidates from the
* fixture's own declared prefixes keeps the match exact in both directions.
*/
function candidateDeclaredPaths(routePath, prefixes) {
	const inApp = normalizeRoutePath(routePath);
	return [inApp, ...prefixes.map((prefix) => normalizeRoutePath(`${prefix}${inApp}`))];
}
/** A declared `methods` list covers this route: absent or empty means every method. */
function methodsCover(declared, httpMethod) {
	if (!Array.isArray(declared) || declared.length === 0) return true;
	const want = httpMethod.toUpperCase();
	return declared.some((m) => typeof m === "string" && m.toUpperCase() === want);
}
/**
* The `expose.unguarded` entry that legalises this route, or `undefined`.
*
* ⚠️ AN ENTRY WITH A BAD `reason` STILL MATCHES HERE. Matching and validity are separated on
* purpose: a matched-but-invalid entry must produce "your reason is too short", which names the
* one-word fix, rather than "this route is undeclared", which sends the author to write a
* declaration they already wrote.
*/
function matchingUnguardedRule(m, route) {
	const declared = m.expose?.unguarded;
	if (!Array.isArray(declared)) return void 0;
	const candidates = new Set(candidateDeclaredPaths(route.routePath, declaredApiPathPrefixes(m)));
	for (const raw of declared) {
		if (raw === null || typeof raw !== "object") continue;
		const rule = raw;
		if (typeof rule.path !== "string") continue;
		if (!candidates.has(normalizeRoutePath(rule.path))) continue;
		if (!methodsCover(rule.methods, route.httpMethod)) continue;
		return rule;
	}
}
/**
* {@link UNGUARDED_REASON_MIN_LENGTH} non-whitespace characters, with any whitespace between them
* (ruling R-35, amended). The same pattern `route-inventory.schema.json` and `fixture.schema.json`
* carry beside `minLength`, so the rules, the two declaration decorators and the inventory schema
* refuse the same reasons: a padded or blank one, and one whose 30 characters are mostly spaces.
*/
const REASON_SUBSTANCE = new RegExp(`^(?:\\s*\\S){30}`);
/** A `reason` is usable when it is a string matching {@link REASON_SUBSTANCE}. */
function isUsableUnguardedReason(reason) {
	return typeof reason === "string" && REASON_SUBSTANCE.test(reason);
}
/**
* The `@Public()` legality verdict for one route, shared by the static gate and the runtime gate.
*
* ⭐ EXPORTED, and it is the reason this module is in `@zion/sdk-contracts` at all. Two gates ask
* this question at two different moments about the same route, and a gate that refuses a boot the
* CLI passed, or the reverse, is worse than either gate alone. One predicate, one answer.
*
* ⛔ ORDER IS LOAD-BEARING. The health probe is checked FIRST, because it is legal with no
* `expose.unguarded` entry at all and needs no reason. Only a route it does not cover reaches the
* `expose.unguarded` walk, so a fixture cannot be made to write a justification for its own health
* probe.
*
* ⚠️ GUARDS ARE NOT CONSIDERED. A route carrying `@Public()` AND an authenticating
* `@UseGuards(...)` is authenticated, and ruling R-8 nonetheless gives three categories and
* "nothing else", so such a route is reported. {@link DiscoveredPublicRoute.guards} carries the
* guard names so the message can state them rather than flatly asserting the route is
* unauthenticated. Measured 2026-09-01: no route of that shape exists in the app repo, so this is
* a latent divergence from the platform's oxlint rule, not a live false positive.
*/
function classifyPublicRoute(manifest, route) {
	if (HEALTH_PROBE_METHODS.has(route.httpMethod.toUpperCase()) && declaredHealthProbePaths(manifest).has(normalizeRoutePath(route.routePath))) return { kind: "healthProbe" };
	const m = manifest !== null && typeof manifest === "object" ? manifest : void 0;
	const matched = m === void 0 ? void 0 : matchingUnguardedRule(m, route);
	if (matched === void 0) return { kind: "undeclared" };
	return isUsableUnguardedReason(matched.reason) ? {
		kind: "declaredUnguarded",
		reason: matched.reason.trim()
	} : {
		kind: "reasonTooShort",
		declaredPath: String(matched.path)
	};
}
/**
* True when this verdict permits the route to ship.
*
* ⭐ A TYPE GUARD, NOT A `boolean`, AND THAT IS WHAT KEEPS THE MESSAGE BUILDER HONEST. Returning a
* plain boolean leaves the caller holding the full union, so the `declaredUnguarded` arm has to
* re-test `verdict.kind` to reach `verdict.reason` and carries a fallback arm that CANNOT be taken.
* Narrowing here makes the switch exhaustive over exactly two kinds with no unreachable branch to
* leave uncovered or suppress.
*/
function isLegalPublicRoute(verdict) {
	return verdict.kind === "healthProbe" || verdict.kind === "declaredUnguarded";
}
/**
* Report every `@Public()` route this fixture does not declare.
*
* SILENT ON THREE SHAPES, all of which are correct code: a route with no `@Public()` (it never
* reaches this function, since the discoverer yields only public ones), a declared health probe,
* and a route declared in `expose.unguarded` with a usable reason. An app with no routes yields no
* issues because `routes` is empty.
*
* ⛔ NEVER REPORTS ON `expose.public`. See this module's header. An app is SUPPOSED to have public
* routes; the question this rule asks is whether the ones that skip the guard are declared.
*
* @param manifest a fixture manifest, or anything at all. Only `services[].health` and
*   `services[].containers[].health` are read.
* @param routes every `@Public()` route a discoverer found for this fixture.
*/
function checkFixturePublicRoutes(manifest, routes) {
	const declared = declaredHealthProbePaths(manifest);
	const issues = [];
	for (const route of routes) {
		const verdict = classifyPublicRoute(manifest, route);
		if (isLegalPublicRoute(verdict)) {
			issues.push({
				severity: "warning",
				path: routeLabel(route),
				rule: PUBLIC_ROUTE_DECLARED_RULE,
				message: publicRouteDeclaredMessage(route, verdict)
			});
			continue;
		}
		issues.push({
			severity: "error",
			path: routeLabel(route),
			rule: PUBLIC_ROUTE_UNDECLARED_RULE,
			message: publicRouteUndeclaredMessage(route, declared, verdict)
		});
	}
	return issues;
}
/** `<file>#<Controller>.<handler>`, sanitized. The issue `path` for both rules in this module. */
function routeLabel(route) {
	return `${safeRuleLabel(route.file)}#${safeRuleLabel(route.controller)}.${safeRuleLabel(route.handler)}`;
}
/**
* The inventory line for a route whose `@Public()` IS declared.
*
* States WHICH of the three declarations made it legal, because "this is fine" without a reason is
* what a reviewer has to re-derive by hand every time. For an `expose.unguarded` route it QUOTES
* the author's own `reason`: that sentence is the entire value of the field, and an inventory that
* withheld it would make the reviewer open the manifest to learn what the line already knows.
*/
function publicRouteDeclaredMessage(route, verdict) {
	let basis;
	switch (verdict.kind) {
		case "healthProbe":
			basis = "it is a health probe this fixture declares";
			break;
		case "declaredUnguarded": basis = `this fixture declares it in expose.unguarded, with the reason: "${safeReason(verdict.reason)}"`;
	}
	return `PUBLIC ROUTE, DECLARED. ${safeRuleLabel(route.httpMethod)} ${safeRuleLabel(route.routePath)} carries @Public() and skips the default auth guard. This is allowed because ${basis}. Found at ${safeRuleLabel(route.file)} in ${safeRuleLabel(route.controller)}.${safeRuleLabel(route.handler)}. Listed so that every route bypassing authentication is visible on every run, not only when one is wrong.`;
}
/**
* The message for an illegal route. Loud, and it names all four things a developer needs: which
* route, where it is, what makes it illegal, and which declaration would make it legal.
*
* ⭐ IT PRINTS WHAT THE FIXTURE DOES DECLARE. A near miss is the common case (`/healthz` declared,
* `/health` served), and a message that lists only the remedy leaves the developer diffing two
* files by eye to find a one-character difference.
*
* ⛔ THE TOO-SHORT ARM SAYS SOMETHING COMPLETELY DIFFERENT, on purpose. See
* {@link PublicRouteVerdict}: an author who already wrote the declaration must be told to expand
* the reason, not told the route is undeclared.
*/
function publicRouteUndeclaredMessage(route, declared, verdict) {
	const what = `${safeRuleLabel(route.httpMethod)} ${safeRuleLabel(route.routePath)}`;
	const at = `Found at ${safeRuleLabel(route.file)} in ${safeRuleLabel(route.controller)}.${safeRuleLabel(route.handler)}`;
	if (verdict.kind === "reasonTooShort") return `PUBLIC ROUTE WITH NO USABLE REASON. ${what} IS declared in expose.unguarded as '${safeRuleLabel(verdict.declaredPath)}', so the declaration is not missing: its reason is. ${at}. A reason must be at least 30 non-whitespace characters, whitespace not counted, because a required field that accepts one word is required in name only. Write what the caller is, why it cannot hold a credential, and what bounds the damage.`;
	const where = route.classLevel ? `the @Public() sits on the controller class ${safeRuleLabel(route.controller)}, so it covers every route in it` : `the @Public() sits on the handler`;
	const declaredList = declared.size === 0 ? "This fixture declares no health-probe path at all." : `This fixture declares these health-probe paths: ${[...declared].sort().join(", ")}.`;
	return `${route.guards.length > 0 ? `UNDECLARED PUBLIC ROUTE. ${what} carries @Public(), which removes the DEFAULT auth guard. It does carry @UseGuards(${route.guards.map((g) => safeRuleLabel(g)).join(", ")}), so it may well be authenticated, but nothing in this fixture declares it and this gate cannot confirm what those guards establish.` : `UNAUTHENTICATED PUBLIC ROUTE. ${what} carries @Public(), which removes the auth guard, nothing in this fixture declares it, and it carries no guard of its own.`} ${at}, where ${where}. ${declaredList} A @Public() route is legal only when the manifest declares it, and there are two ways. If it is a GET health probe, add its path to a service's health.ready or health.live. Otherwise declare it in expose.unguarded as { path, methods?, reason } with a reason of at least 30 characters saying why this route cannot require a credential — and that is the route to take for an OAuth protected-resource metadata document too, which no longer has an exemption of its own. If neither is true, remove @Public() and let the route be guarded.`;
}
//#endregion
export { PUBLIC_ROUTE_DECLARED_RULE, PUBLIC_ROUTE_UNDECLARED_RULE, REASON_SUBSTANCE, UNGUARDED_REASON_MIN_LENGTH, checkFixturePublicRoutes, classifyPublicRoute, declaredApiPathPrefixes, declaredHealthProbePaths, isLegalPublicRoute, isUsableUnguardedReason, normalizeRoutePath };

//# sourceMappingURL=fixture-public-route-rules.js.map